import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldPath,
  FieldValues
} from 'react-hook-form'
import { cn } from '../../../utils'
import ReadOnlyText from './ReadOnlyText'
import { useBefehlFormConfigIfAvailable } from './BefehlFormConfigContext'

export interface BefehlFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName
  control: Control<TFieldValues>
  label: string
  readOnly?: boolean
  activateEmptyWarning?: boolean
  className?: string
  'data-testid': string
  disabled?: boolean
  variant?: 'semitransparent' | 'white' | 'solid' | 'outline'
  isTextArea?: boolean
  useTextDisplayInReadOnly?: boolean
  render: (props: {
    field: ControllerRenderProps<TFieldValues, TName>
    isFieldEmpty: boolean
    isDisabled: boolean
    isReadOnly: boolean
  }) => React.ReactElement | null
}

export function BefehlFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  label,
  readOnly: readOnlyProp,
  activateEmptyWarning: activateEmptyWarningProp,
  className,
  disabled = false,
  variant = 'solid',
  isTextArea = false,
  useTextDisplayInReadOnly = true,
  render,
  'data-testid': testId
}: Readonly<BefehlFormFieldProps<TFieldValues, TName>>) {
  const formConfig = useBefehlFormConfigIfAvailable()

  const readOnly = readOnlyProp ?? formConfig?.readOnly ?? false
  const activateEmptyWarning =
    activateEmptyWarningProp ?? formConfig?.activateEmptyFieldWarnings ?? false
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const isFieldEmpty =
          typeof field.value === 'boolean'
            ? false
            : field.value === null ||
              field.value === undefined ||
              (typeof field.value === 'string' && field.value.trim() === '')

        if (
          readOnly &&
          useTextDisplayInReadOnly &&
          typeof field.value !== 'boolean'
        ) {
          return (
            <div
              className={cn('w-full min-w-8', className)}
              data-testid={`${testId}-container`}
            >
              <ReadOnlyText
                value={field.value}
                label={label}
                variant={variant}
                activateEmptyWarning={activateEmptyWarning && isFieldEmpty}
                isMultiline={isTextArea}
                data-testid={testId}
              />
            </div>
          )
        }

        const rendered = render({
          field,
          isFieldEmpty,
          isDisabled: disabled,
          isReadOnly: readOnly
        })

        return (
          <>
            {rendered ? (
              <div className={className} data-testid={`${testId}-container`}>
                {rendered}
              </div>
            ) : null}
          </>
        )
      }}
    />
  )
}
